/* ============================================================
   LiquorVerse cart engine (cart-drawer-v2 / cart-page-v3).
   Single source of cart mutations:
     - LVCart.add(variantId, qty)   → /cart/add.js
     - LVCart.setLine(line, qty)    → /cart/change.js
   Every mutation requests re-rendered sections (Section
   Rendering API) so all money/meter/summary markup stays
   server-computed Liquid, then dispatches 'lv:cart:updated'
   {total_cents, item_count} for meters (§2.5) and nav counts.
   Event delegation throughout — re-rendered DOM needs no rebinding.
   ============================================================ */
(function () {
  'use strict';

  var DRAWER_SECTION = 'lv-cart-drawer';

  function cartPageSectionId() {
    var el = document.querySelector('[data-lv-cart-page-section]');
    return el ? el.dataset.lvCartPageSection : null;
  }

  function sectionsToRender() {
    var ids = [DRAWER_SECTION];
    var pageId = cartPageSectionId();
    if (pageId) ids.push(pageId);
    return ids;
  }

  function swapSection(id, html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var freshInner = doc.getElementById('shopify-section-' + id);
    var current = document.getElementById('shopify-section-' + id);
    if (freshInner && current) {
      var wasOpen = false;
      var drawer = current.querySelector('[data-lv-cart-drawer]');
      if (drawer) wasOpen = drawer.classList.contains('open');
      current.innerHTML = freshInner.innerHTML;
      if (wasOpen) {
        var freshDrawer = current.querySelector('[data-lv-cart-drawer]');
        var freshScrim = current.querySelector('[data-lv-cart-scrim]');
        if (freshDrawer) {
          freshDrawer.classList.add('open');
          freshDrawer.setAttribute('aria-hidden', 'false');
        }
        if (freshScrim) freshScrim.classList.add('show');
      }
    }
  }

  function applyCart(cart, sections) {
    if (sections) {
      Object.keys(sections).forEach(function (id) {
        swapSection(id, sections[id]);
      });
    }
    document.querySelectorAll('[data-lv-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
    });
    readCartState(cart);
    document.dispatchEvent(new CustomEvent('lv:cart:updated', {
      detail: { total_cents: cart.total_price, item_count: cart.item_count }
    }));
  }

  /* ---- Persistent "in cart" state on quick-add buttons ----
     The 1.8s flash tells you the click worked; it does not tell you, three rows later,
     that you already added this bottle. These buttons now reflect the actual cart and keep
     doing so — repainted from every cart mutation and once on load.

     Only buttons carrying [data-lv-add-label] are touched, so the plain-text add buttons
     (mini-add, barrel compare, matchmaker, creator) are unaffected.

     aria-label is updated as well as the label text: an aria-label OVERRIDES an element's
     contents for assistive tech, so changing only the visible words would have left screen
     readers hearing "Quick add" for a bottle already in the cart. */
  var cartQtyByVariant = null;
  var cartLineByVariant = null;   /* variant id -> 1-based cart line, for setLine() */

  function paintAddButtons() {
    if (!cartQtyByVariant) return;
    document.querySelectorAll('[data-lv-add-id]').forEach(function (btn) {
      var labelEl = btn.querySelector('[data-lv-add-label]');
      if (!labelEl) return;
      if (!btn.dataset.lvIdleLabel) btn.dataset.lvIdleLabel = labelEl.textContent;
      if (!btn.dataset.lvBaseAria) btn.dataset.lvBaseAria = btn.getAttribute('aria-label') || '';

      var qty = cartQtyByVariant[String(btn.dataset.lvAddId)] || 0;
      btn.classList.toggle('is-in-cart', qty > 0);

      /* the transient flash owns the label while it runs */
      if (btn.classList.contains('is-added')) return;

      if (qty > 0) {
        labelEl.textContent = qty > 1 ? 'In cart (' + qty + ')' : 'In cart';
        btn.setAttribute('aria-label', btn.dataset.lvBaseAria + ' — already in your cart, add another');
      } else {
        labelEl.textContent = btn.dataset.lvIdleLabel;
        btn.setAttribute('aria-label', btn.dataset.lvBaseAria);
      }
    });
  }

  /* The PDP's own "in your cart" line. Same source of truth as the card buttons: the real
     cart, repainted on every mutation and once on load. The button flash lasts under two
     seconds and the drawer is off-canvas, so without this a phone showed nothing at all
     afterwards — which is exactly what Ethan reported. */
  /* The add row and the in-cart row are the SAME control in two states, so exactly one
     of them is on screen [Ethan 2026-08-09]. Previously both rendered at once: a bottle
     already in the cart showed "Claim your bottle — $51.99" stacked on top of "In your
     cart − 2 +", which invites a tap that silently adds a third. Once it is in the cart
     the stepper is the correct affordance, and it edits the real cart line. */
  function setAddRowHidden(form, hide) {
    if (!form || !form.querySelector) return;
    var addRow = form.querySelector('.lv-qty-row');
    if (addRow) addRow.hidden = hide;
  }

  function paintAtcStatus() {
    if (!cartQtyByVariant) return;
    document.querySelectorAll('[data-lv-atc-status]').forEach(function (el) {
      var form = el.closest('form') || document;
      var idInput = form.querySelector('[name="id"]');
      if (!idInput) { el.hidden = true; return; }
      var vid = String(idInput.value);
      var qty = cartQtyByVariant[vid] || 0;
      /* Not in the cart (including straight after Remove) — hand the row back. */
      if (!qty) { el.hidden = true; el.innerHTML = ''; setAddRowHidden(form, false); return; }
      setAddRowHidden(form, true);

      var maxAttr = form.querySelector('[data-lv-qty-max]');
      var max = maxAttr ? parseInt(maxAttr.getAttribute('data-lv-qty-max'), 10) : 0;
      var atMax = max > 0 && qty >= max;

      el.dataset.lvLine = cartLineByVariant[vid] || '';
      el.innerHTML =
        '<svg class="lv-atc-tick" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" ' +
          'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>' +
        '<span class="lv-atc-label">In your cart</span>' +
        '<span class="lv-atc-qty">' +
          '<button type="button" data-lv-line-dec aria-label="Decrease quantity in cart">–</button>' +
          '<span aria-live="polite">' + qty + '</span>' +
          '<button type="button" data-lv-line-inc aria-label="Increase quantity in cart"' +
            (atMax ? ' disabled' : '') + '>+</button>' +
        '</span>' +
        '<button type="button" class="lv-atc-remove" data-lv-line-remove>Remove</button>';
      el.hidden = false;
    });
  }

  /* The in-cart control edits the real cart line rather than the form's quantity field —
     the form only ever adds one. Delegated so it survives every repaint. */
  document.addEventListener('click', function (e) {
    var host = e.target.closest('[data-lv-atc-status]');
    if (!host) return;
    var line = parseInt(host.dataset.lvLine, 10);
    if (!line) return;
    var cur = parseInt(host.querySelector('.lv-atc-qty span').textContent, 10) || 0;
    if (e.target.closest('[data-lv-line-inc]')) { setLine(line, cur + 1); return; }
    if (e.target.closest('[data-lv-line-dec]')) { setLine(line, Math.max(0, cur - 1)); return; }
    if (e.target.closest('[data-lv-line-remove]')) { setLine(line, 0); return; }
  });

  function readCartState(cart) {
    cartQtyByVariant = {};
    cartLineByVariant = {};
    (cart.items || []).forEach(function (item, i) {
      var id = String(item.variant_id || item.id);
      cartQtyByVariant[id] = (cartQtyByVariant[id] || 0) + item.quantity;
      /* Cart AJAX line numbers are 1-based and positional. */
      if (!cartLineByVariant[id]) cartLineByVariant[id] = i + 1;
    });
    paintAddButtons();
    paintAtcStatus();
  }

  function request(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  var busy = false;

  function add(variantId, qty) {
    if (busy) return Promise.resolve();
    busy = true;
    return request(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      items: [{ id: parseInt(variantId, 10), quantity: qty || 1 }],
      sections: sectionsToRender().join(',')
    }).then(function (res) {
      /* add.js returns the added items + sections; fetch full cart for totals */
      stampOrigin();
      return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        applyCart(cart, res.sections);
        openDrawer();
      });
    }).finally(function () { busy = false; });
  }

  /* Return to origin (§6/§13): record which page drove this add as cart attributes
     _lv_origin / _lv_origins, so the order says which Brand Store (or Verse, or search)
     sold the bottle. Fire-and-forget — a failed stamp must never block an add. */
  function stampOrigin() {
    try { if (window.LVOrigin) window.LVOrigin.stamp(); } catch (e) {}
  }

  /* Bottle add-ons [2026-10-29]: engraving and gift-sleeve lines are tied to their bottle
     (assets/lv-addons.js). When that script is on the page it gets first look at every
     quantity change: it returns a plan — {updates: {lineKey: qty}, after: [change.js bodies]}
     — that moves the bottle and its add-ons together, or null when the line has nothing
     tied to it, in which case the plain single-line change runs exactly as before. */
  function setLine(line, qty) {
    if (busy) return Promise.resolve();
    busy = true;
    var n = parseInt(line, 10);
    var q = Math.max(0, parseInt(qty, 10) || 0);
    var planner = window.LVAddons && window.LVAddons.plan;
    var planned = planner
      ? fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { return planner(cart, n, q); })
          .catch(function () { return null; })
      : Promise.resolve(null);
    return planned.then(function (plan) {
      if (plan) return runPlan(plan);
      return request('/cart/change.js', {
        line: n,
        quantity: q,
        sections: sectionsToRender().join(',')
      }).then(function (cart) {
        applyCart(cart, cart.sections);
      });
    }).finally(function () { busy = false; });
  }

  /* Apply a multi-line plan: one /cart/update.js for every quantity, then any property
     changes (e.g. clearing engraving text off a bottle) in order. Only the final request
     asks for re-rendered sections, so the cart repaints once, already consistent. */
  function runPlan(plan) {
    var secs = sectionsToRender().join(',');
    var after = plan.after || [];
    var body = { updates: plan.updates || {} };
    if (!after.length) body.sections = secs;
    var p = request('/cart/update.js', body);
    after.forEach(function (ch, i) {
      p = p.then(function () {
        var b = {};
        Object.keys(ch).forEach(function (k) { b[k] = ch[k]; });
        if (i === after.length - 1) b.sections = secs;
        return request('/cart/change.js', b);
      });
    });
    return p.then(function (cart) { applyCart(cart, cart.sections); });
  }

  /* ---- drawer open/close (delegated — survives re-render) ---- */
  function drawerEls() {
    return {
      drawer: document.querySelector('[data-lv-cart-drawer]'),
      scrim: document.querySelector('[data-lv-cart-scrim]')
    };
  }
  function openDrawer() {
    var els = drawerEls();
    if (!els.drawer) return;
    els.drawer.classList.add('open');
    els.drawer.setAttribute('aria-hidden', 'false');
    if (els.scrim) els.scrim.classList.add('show');
    document.body.style.overflow = 'hidden';
    /* Third-party floaters (Accessibly button, Stamped REVIEWS tab) sit on top
       of the drawer and cover its contents — CSS keyed on this class hides
       them while the drawer is open [Ethan 2026-08-07]. */
    document.documentElement.classList.add('lv-cart-open');
  }
  function closeDrawer() {
    var els = drawerEls();
    if (!els.drawer) return;
    els.drawer.classList.remove('open');
    els.drawer.setAttribute('aria-hidden', 'true');
    if (els.scrim) els.scrim.classList.remove('show');
    document.body.style.overflow = '';
    document.documentElement.classList.remove('lv-cart-open');
  }

  /* POST that NEVER rejects — resolves {ok, status, data}. Sibling of
     request(), not a replacement: request()'s callers (add/setLine) are
     untouched in this pass and migrate later. Added for addMany because
     /cart/add.js answers a sold-out multi-item payload with a 422 whose
     body parses fine — so the old code resolved "success", re-rendered an
     unchanged cart, and opened the drawer on it. */
  function postCart(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json()
        .catch(function () { return null; })
        .then(function (data) { return { ok: r.ok, status: r.status, data: data }; });
    }).catch(function () { return { ok: false, status: 0, data: null }; });
  }

  /* Add many variants in one request (one-tap reorder, Bar Cart Builder).
     items = [{id, quantity}]. NEVER rejects — resolves a result object:
       {ok:true,  status, added:[id…], failed:[]}
       {ok:false, status:422, added:[], failed:[{id,reason}], description}
       {ok:false, reason:'busy'}      ← was a silent Promise.resolve()
       {ok:false, reason:'network', status:0}
     /cart/add.js is ALL-OR-NOTHING on multi-item payloads: a 422 means
     nothing was added. Callers that ignore the resolved value (the account
     page's one-tap reorder) keep working — a failure now reaches their
     .then instead of stranding a disabled button on an unhandled reject. */
  function addMany(items) {
    if (!items || !items.length) return Promise.resolve({ ok: false, reason: 'empty', added: [], failed: [] });
    if (busy) return Promise.resolve({ ok: false, reason: 'busy', added: [], failed: [] });
    busy = true;
    return postCart('/cart/add.js', {
      items: items,
      sections: sectionsToRender().join(',')
    }).then(function (res) {
      if (!res.ok) {
        busy = false;
        if (res.status === 0) return { ok: false, reason: 'network', status: 0, added: [], failed: [] };
        return {
          ok: false, status: res.status, added: [],
          failed: items.map(function (i) { return { id: i.id, reason: 'rejected' }; }),
          description: res.data && res.data.description ? res.data.description : ''
        };
      }
      stampOrigin();
      return fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          applyCart(cart, res.data && res.data.sections);
          openDrawer();
          return {
            ok: true, status: res.status,
            added: items.map(function (i) { return i.id; }), failed: []
          };
        })
        .catch(function () {
          /* the add itself succeeded; only the refresh failed */
          return { ok: true, status: res.status, added: items.map(function (i) { return i.id; }), failed: [] };
        })
        .finally(function () { busy = false; });
    });
  }

  /* One read on load so a page opened with a non-empty cart already shows which bottles
     are in it — otherwise the state only appeared after the first add of the session. */
  function primeCartState() {
    /* Anything that reflects cart state counts — the card quick-add buttons AND the PDP's
       in-cart control. Checking only for [data-lv-add-id] meant a PDP never read the cart
       on load, so the control stayed hidden even with the bottle already in the cart. */
    if (!document.querySelector('[data-lv-add-id], [data-lv-atc-status]')) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(readCartState)
      .catch(function () { /* offline or blocked — buttons just stay in their idle state */ });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', primeCartState);
  } else {
    primeCartState();
  }

  window.LVCart = { add: add, addMany: addMany, setLine: setLine, runPlan: runPlan, open: openDrawer, close: closeDrawer };

  document.addEventListener('click', function (e) {
    /* qty steppers + remove in drawer/page line items */
    var lineBtn = e.target.closest('[data-lv-line]');
    if (lineBtn) {
      setLine(lineBtn.dataset.lvLine, lineBtn.dataset.lvQtySet);
      return;
    }
    /* Quick add — "Room in the Box" upsells and product-card quick-add buttons.
       Both just declare a variant id and let this do the work. */
    var addBtn = e.target.closest('[data-lv-add-id]');
    if (addBtn) {
      /* Acknowledge in place. A quick add from deep in a long grid opens the
         drawer off to the side, which is easy to miss mid-scroll; the button
         itself confirming is the feedback that actually gets seen. */
      /* Only ever rewrite a dedicated label element. Setting textContent on the BUTTON
         wiped its child SVG — on the phone card, where the control is an icon in a 36px
         box, that replaced the glyph with cramped raw text and the icon never came back,
         because the restore wrote the flattened string. Buttons that are plain text
         (mini-add, compare, matchmaker) have no label element and keep the old path. */
      var labelEl = addBtn.querySelector('[data-lv-add-label]');
      var addedText = addBtn.dataset.lvAddedLabel || 'Added ✓';
      addBtn.classList.add('is-added');
      var restore;
      if (labelEl) {
        labelEl.textContent = addedText;
        /* repaint from cart state rather than restoring the old string — by the time the
           flash ends this bottle IS in the cart, and the label should say so. */
        restore = paintAddButtons;
      } else {
        var prevText = addBtn.textContent;
        addBtn.textContent = addedText;
        restore = function () { addBtn.textContent = prevText; };
      }
      setTimeout(function () {
        addBtn.classList.remove('is-added');
        restore();
      }, 1800);
      add(addBtn.dataset.lvAddId, 1);
      return;
    }
    /* drawer close + scrim */
    if (e.target.closest('[data-lv-cart-close]') || e.target.closest('[data-lv-cart-scrim]')) {
      closeDrawer();
      return;
    }
    /* Nav cart link opens the drawer instead of navigating (except on the cart
       page itself).

       The selector is href-based, so it also caught the drawer's OWN "View full
       cart" link [Ethan 2026-08-09] — clicking it called preventDefault and then
       re-opened an already-open drawer, i.e. a dead link on every breakpoint.
       A cart link INSIDE the drawer has to navigate: escaping to the full cart
       page is the entire reason it is there. Same for the cart page's own links,
       already handled by the section check. */
    var cartLink = e.target.closest('a[href$="/cart"]');
    if (cartLink &&
        !cartLink.closest('[data-lv-cart-drawer]') &&
        !document.querySelector('[data-lv-cart-page-section]')) {
      e.preventDefault();
      openDrawer();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ATC form intercept (PDP, club shelf) → AJAX add + drawer */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form.action && form.action.indexOf('/cart/add') !== -1)) return;
    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return;
    e.preventDefault();
    var qtyInput = form.querySelector('[name="quantity"]');

    /* Same acknowledge-in-place treatment the card buttons get. Only the label span is
       rewritten — writing textContent on the button would delete the price span. */
    var btn = form.querySelector('button[type="submit"]');
    var lbl = btn && btn.querySelector('[data-lv-add-label]');
    if (btn && lbl) {
      btn.classList.add('is-added');
      var prev = lbl.textContent;
      lbl.textContent = 'Added ✓';
      setTimeout(function () {
        btn.classList.remove('is-added');
        lbl.textContent = prev;
      }, 1800);
    }

    add(idInput.value, qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1);
  });
})();
